import type { Edge, Graph, Vertex } from '@metro/graph'
import type { AlgorithmConfig } from './config'
import { aStar } from './astar'

export function yenK(
  graph: Graph,
  source: Vertex,
  target: Vertex,
  k: number,
  config: AlgorithmConfig,
): Edge[][] {
  if (k <= 0) return []
  let first: Edge[]
  try {
    first = aStar(graph, source, target, config)
  } catch {
    return []
  }
  const result: Edge[][] = [first]

  type Candidate = { edges: Edge[]; weight: number; signature: string }
  const candidates: Candidate[] = []
  const seenSig = new Set<string>([pathSignature(first)])

  for (let i = 1; i < k; i++) {
    const prev = result[i - 1]
    for (let j = 0; j < prev.length; j++) {
      const spurNode = prev[j].from
      const rootPath = prev.slice(0, j)

      const deactivated: Edge[] = []
      for (const p of result) {
        if (p.length > j && pathPrefixEqual(p.slice(0, j), rootPath)) {
          deactivated.push(p[j])
          graph.deactivate(p[j])
        }
      }
      const deactivatedAt: number[] = []
      for (const e of rootPath) {
        if (!(e.from.id === spurNode.id && e.from.lineID === spurNode.lineID)) {
          const from = graph.vertexIndex(e.from)
          if (from === undefined) continue
          for (const outgoing of graph.outgoingEdges(from)) {
            if (!graph.isActive(outgoing)) continue
            deactivatedAt.push(outgoing)
            graph.deactivateAt(outgoing)
          }
        }
      }

      let spur: Edge[] | null = null
      try {
        spur = aStar(graph, spurNode, target, config)
      } catch {
        spur = null
      }

      for (const e of deactivated) graph.restore(e)
      for (const e of deactivatedAt) graph.restoreAt(e)

      if (spur === null) continue
      const full = [...rootPath, ...spur]
      const sig = pathSignature(full)
      if (seenSig.has(sig)) continue
      seenSig.add(sig)
      candidates.push({ edges: full, weight: pathWeight(full), signature: sig })
    }
    if (candidates.length === 0) break
    // Equal weights fall back to the path signature: the same order on every platform.
    candidates.sort((a, b) => a.weight - b.weight || compareSignatures(a.signature, b.signature))
    result.push(candidates.shift()!.edges)
  }
  return result
}

function compareSignatures(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

export function pathSignature(edges: readonly Edge[]): string {
  return edges.map((e) => e.realID).join('>')
}

export function pathPrefixEqual(a: readonly Edge[], b: readonly Edge[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i].realID !== b[i].realID) return false
  return true
}

export function pathWeight(edges: readonly Edge[]): number {
  let total = 0
  for (const e of edges) total += e.weight
  return total
}
